import { Data, UI, Utils } from 'common';
import React from 'react';
import { ConfirmationState, Plan } from './hooks/useSubscriptionPlanState';
import { getPlanSummarySource, parsePlanSummary } from './planRules';
import SeatStepper from './SeatStepper.component';

const { Badge, Typography, WrappedButton, WrappedButtonType } = UI;
const { BillingPlans } = Utils;
const { PlanIds } = Data;

interface SubscriptionCardProps {
	plan: Plan;
	isCurrentPlan: boolean;
	isSelected: boolean;
	seatCount: number;
	onSelect: (planId: number) => void;
	onSeatChange: (planId: number, count: number) => void;
	seatPrice?: number;
	focusSeatInput?: boolean;
	cardRef?: React.Ref<HTMLDivElement>;
	readOnly?: boolean;
	showUpdateButton?: boolean;
	onUpdatePlan?: () => void;
	isUpdating?: boolean;
	confirmationState?: ConfirmationState;
	isCancelled?: boolean;
	isNewLineup?: boolean;
	isLegacyPlan?: boolean;
}

const SubscriptionCard = ({
	plan,
	isCurrentPlan,
	isSelected,
	seatCount,
	onSelect,
	onSeatChange,
	seatPrice,
	focusSeatInput = false,
	cardRef,
	readOnly = false,
	showUpdateButton = false,
	onUpdatePlan,
	isUpdating = false,
	confirmationState = 'idle',
	isCancelled = false,
	isNewLineup: isNewLineupProp,
	isLegacyPlan = false,
}: SubscriptionCardProps) => {
	const displayName =
		plan.DisplayName || BillingPlans.prettyPrintPlan(plan.Name);
	const tagline = plan.Tagline || '';
	const includedSeats = plan.NumberOfLocations ?? 1;
	const isNewLineup = isNewLineupProp ?? BillingPlans.isNewLineupPlan();
	const {
		tag: planTag,
		subHeader: planSubHeader,
		features,
	} = parsePlanSummary(getPlanSummarySource(plan));

	const showCurrentPlanBadge =
		isCurrentPlan &&
		(isCancelled || !BillingPlans.pendingPlanWasAutoSelectedOnRegistration);
	const showPopularBadge =
		!isCurrentPlan && plan.PlanId === PlanIds.Professional403;
	// Cards with no badge (e.g. Multi-Location) still need one reserved so the
	// title row is the same height as cards that render a real badge — a
	// hidden placeholder guarantees that by construction instead of guessing.
	const showAnyBadge = showCurrentPlanBadge || showPopularBadge;

	const isInteractive = !readOnly && isNewLineup;

	const handleCardClick = () => {
		if (isInteractive) onSelect(plan.PlanId);
	};

	const handleCardKeyDown = (e: React.KeyboardEvent) => {
		if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
			e.preventDefault();
			onSelect(plan.PlanId);
		}
	};

	const handleSeatChange = (value: number) => {
		onSeatChange(plan.PlanId, value);
	};

	const cardClassName = [
		'subscription-card',
		isSelected ? 'subscription-card--selected' : '',
		isCurrentPlan ? 'subscription-card--current' : '',
		!isInteractive ? 'subscription-card--readonly' : '',
	]
		.filter(Boolean)
		.join(' ');

	const renderCtaButton = () => {
		// On a cancelled account, every plan must be selectable so the user can
		// reactivate by picking any plan (including the current one).
		if (isCurrentPlan && !isCancelled) {
			const label = isSelected ? 'Current' : 'Select';
			return (
				<WrappedButton
					buttonType={
						isSelected ? WrappedButtonType.Primary : WrappedButtonType.Secondary
					}
					label={label}
					disabled={isSelected}
					dataqa={`subscription-card-current-${plan.PlanId}`}
					className="subscription-card__cta subscription-card__cta--current"
					onClick={(e: React.MouseEvent) => {
						e.stopPropagation();
						onSelect(plan.PlanId);
					}}
				/>
			);
		}

		if (isNewLineup) {
			if (readOnly) return null;

			const label = isSelected ? 'Selected' : 'Select';

			return (
				<WrappedButton
					buttonType={
						isSelected ? WrappedButtonType.Primary : WrappedButtonType.Secondary
					}
					label={label}
					disabled={isSelected}
					dataqa={`subscription-card-select-${plan.PlanId}`}
					className="subscription-card__cta"
					onClick={(e: React.MouseEvent) => {
						e.stopPropagation();
						onSelect(plan.PlanId);
					}}
				/>
			);
		}

		if (showUpdateButton) {
			let label = 'Select';
			if (isUpdating && confirmationState === 'success')
				label = 'Plan Updated!';
			else if (isUpdating) label = 'Updating...';

			return (
				<WrappedButton
					buttonType={WrappedButtonType.Secondary}
					label={label}
					disabled={isUpdating}
					dataqa={`subscription-card-update-${plan.PlanId}`}
					className="subscription-card__cta"
					onClick={(e: React.MouseEvent) => {
						e.stopPropagation();
						if (!isSelected) onSelect(plan.PlanId);
						onUpdatePlan?.();
					}}
				/>
			);
		}

		return null;
	};

	return (
		<div
			ref={cardRef}
			className={cardClassName}
			{...(isInteractive && { role: 'button', tabIndex: 0 })}
			aria-disabled={!isInteractive || undefined}
			onClick={handleCardClick}
			onKeyDown={handleCardKeyDown}
			data-qa={`subscription-card-${displayName.toLowerCase().replace(/\s+/g, '-')}`}
		>
			<div className="subscription-card__header">
				<div className="subscription-card__title-row">
					<Typography.H4 className="subscription-card__name">
						{displayName}
					</Typography.H4>
					{showCurrentPlanBadge && (
						<div className="subscription-card__badge">
							<Badge
								value="Current Plan"
								type="active"
								badgeStyle="solid"
								size="condensed"
								dataqa="subscription-card-badge-current"
							/>
						</div>
					)}
					{showPopularBadge && (
						<div className="subscription-card__badge">
							<Badge
								value="Recommended"
								type="progress"
								badgeStyle="solid"
								size="condensed"
								dataqa="subscription-card-badge-popular"
							/>
						</div>
					)}
					{!showAnyBadge && (
						<div
							className="subscription-card__badge subscription-card__badge--hidden"
							aria-hidden="true"
						>
							<Badge value="placeholder" size="condensed" />
						</div>
					)}
				</div>
				<div className="subscription-card__price">
					<span className="subscription-card__price-amount">
						${plan.Price.toFixed(2)}
					</span>
					<span className="subscription-card__price-period">/mo</span>
				</div>
				<Typography.P className="subscription-card__tagline">
					{tagline || planTag}
				</Typography.P>
			</div>

			{isNewLineup && (
				<SeatStepper
					value={seatCount}
					min={includedSeats}
					onChange={handleSeatChange}
					seatPrice={seatPrice}
					autoFocus={!readOnly && focusSeatInput && isCurrentPlan}
					highlight={!readOnly && focusSeatInput && isCurrentPlan}
					dataqa={`seat-stepper-${displayName.toLowerCase()}`}
					disabled={readOnly || isLegacyPlan}
					legacyMode={isLegacyPlan}
				/>
			)}

			{renderCtaButton()}

			<div className="subscription-card__features">
				{(plan.InheritLabel || planSubHeader) && (
					<div className="subscription-card__inherit-label">
						{plan.InheritLabel || planSubHeader}
					</div>
				)}
				<ul className="subscription-card__feature-list">
					{features.map(feature => (
						<li key={feature} className="subscription-card__feature-item">
							<span className="subscription-card__check-icon" />
							<span>{feature}</span>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};

export default SubscriptionCard;
